import { useState } from 'react';
import styled from '@emotion/styled/macro';
import { Draggable } from 'react-beautiful-dnd';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { colors, fonts, animate, leading } from '../../styles/theme';
import { Icon } from '../../ui_elements/icons';

const ContentWrapper = styled('div')`
  border: 1px solid ${colors.grey.secondaryDark};
  box-shadow: 0 0 2px 0 ${colors.grey.secondaryDark};
  padding: 15px;
  background: ${colors.white};
  text-align: left;
  border-radius: 3px;
  transition: border-color ${animate.transition} linear;
  position: relative;
`;

const DeleteIcon = styled(Icon)`
  display: ${({ hide }) => (hide ? 'none' : 'block')};
  right: 0;
  opacity: 1;
  &:hover {
    cursor: pointer;
    & ~ ${ContentWrapper} {
      border-color: ${colors.red.secondary};
    }
  }
`;

const HandleIcon = styled(Icon)`
  left: 0;
  color: ${colors.grey.primaryLight};
  &:hover {
    cursor: move;
    color: ${colors.grey.primary};
    & ~ ${ContentWrapper} {
      border-color: ${colors.blue.primary};
    }
  }
`;

const DraggableContainer = styled('div')`
  position: relative;
  padding: 0 25px;
  margin-bottom: ${leading.full};

  ${({ isDragging }) =>
    isDragging
      ? `
    ${ContentWrapper} {
      border: 1px solid ${colors.blue.primaryLight};
    }
    ${DeleteIcon} {
      opacity: 0;
    }
  `
      : ''};

  ${({ canBeEmpty }) =>
    !canBeEmpty
      ? `
      &:only-child {
        padding: 0;
        ${HandleIcon},
        ${DeleteIcon} {
          display: none;
        }
      }
    `
      : ''};

  ${HandleIcon},
  ${DeleteIcon} {
    position: absolute;
    top: 15px;
    font-size: ${fonts.sans.size.normal};
    outline: 0;
    transition: all ${animate.transition} linear;
  }
`;

const DraggableRow = ({ onDelete, hideDeleteIcon, canBeEmpty, child, index }) => {
  const [draggableId] = useState(_.uniqueId('draggable-row-'));

  return (
    <Draggable draggableId={draggableId} index={index}>
      {(provided, snapshot) => (
        <DraggableContainer
          canBeEmpty={canBeEmpty}
          ref={provided.innerRef}
          isDragging={snapshot.isDragging}
          {...provided.draggableProps}
        >
          <HandleIcon type="move" {...provided.dragHandleProps} />
          <DeleteIcon type="x" hide={hideDeleteIcon} onClick={() => onDelete(index)} />
          <ContentWrapper>{child}</ContentWrapper>
        </DraggableContainer>
      )}
    </Draggable>
  );
};

DraggableRow.propTypes = {
  hideDeleteIcon: PropTypes.bool.isRequired,
  canBeEmpty: PropTypes.bool.isRequired,
  onDelete: PropTypes.func,
  child: PropTypes.node.isRequired,
  index: PropTypes.number.isRequired,
};

DraggableRow.defaultProps = {
  onDelete: () => {},
};

export default DraggableRow;
